/**
 * THE PAGE RUNTIME (STATES_AND_INTERACTIVE_ELEMENTS_PLAN §13.6).
 *
 * One small vanilla script for every site that shows builder pages: Budflow's
 * own renders, both storefronts, and a client's plain-HTML site. It is built
 * from this file (with conditions / store-status / match-path) into one
 * script by `scripts/build-page-runtime.mjs`; Budflow inlines the result and
 * each storefront carries a bundled copy, never a hand-written one.
 *
 * Jobs:
 *  1. Disabled is real: click, Enter and Space on `[aria-disabled="true"]`
 *     are stopped before anything else hears them (window, capture phase).
 *     The element keeps its href and its place in the tab order.
 *  2. Live states: every `[data-pb-when]` is re-checked each minute, when the
 *     visitor changes store, and when the tab comes back.
 *  3. Popups and drawers: the trigger's `aria-expanded` follows the dialog it
 *     opened, back to "false" when that dialog closes.
 *  4. Plain-HTML sites only (`host: 'html'`): `aria-current` follows history
 *     navigation, and a link's page is prefetched on hover. React sites do
 *     both themselves.
 *  6. Play triggers (§12 E): an animation or count-up held at its start plays
 *     when its event begins — hover, press, focus, or a state on the element
 *     it follows — and plays back (or resets) when it ends (play-trigger.ts).
 *  5. Client navigation (§13.7): a React host registers a navigator; a click
 *     on a builder page link (`[data-pb-current]`) then moves within the app
 *     instead of loading a new document, and hovering or focusing it
 *     prefetches. After the host renders a new page it calls `pageChanged()`:
 *     focus moves to the new page's heading and its title is announced, so
 *     a screen-reader user isn't left on a link that no longer exists.
 *  7. Loops (loop-effect.ts): a `[data-pb-loop="pause"]` element plays only
 *     while on screen (`data-pb-inview`, under `.pb-loop-ready`).
 *  8. Slide shows (slides-effect.ts, slides.ts): a `[data-pb-slides]` box
 *     steps its strip, with the designer's own arrows and dots.
 *  9. Cycle words (style-system/cycle-words.ts, cycle-words.ts): a
 *     `[data-pb-cycle]` text types each word of its list in turn.
 *
 * Page data: `<script type="application/json" id="pb-live">` —
 * `{ host, timezone, stores: [{ id, schedule }], siteHosts, redirects }`.
 * Missing is fine: with no data every store condition is simply not met.
 */
import { evaluateStateWhen, readStateWhen, type ActiveStore } from './conditions';
import { matchPath, normaliseRedirects } from './match-path';
import type { StoreSchedule } from './store-status';
import type { Crumb } from './breadcrumb-trail';
import { countParts, formatCount, playSource, stateIsOn, type CountParts } from './play-trigger';
import { bindSlideShows } from './slides';
import { bindCycleWords, CYCLE_SELECTOR } from './cycle-words';

export interface PageLiveData {
  host?: 'react' | 'html';
  /** The page's breadcrumb trail (breadcrumb-trail.ts), resolved by the loader. */
  trail?: Crumb[];
  timezone?: string;
  stores?: Array<{ id: string; schedule?: StoreSchedule | null }>;
  siteHosts?: string[];
  redirects?: Record<string, string>;
}

/** A React host's router, for client navigation (§13.7). */
export interface PageNavigator {
  navigate: (href: string) => void;
  prefetch?: (href: string) => void;
}

interface RuntimeApi {
  /** Re-check every live state now (a host calls this after it swaps content). */
  refresh: () => void;
  /** Replace the page data (a React host after client navigation). */
  setData: (data: PageLiveData) => void;
  /** Hand builder page links to the host's router; null hands them back. */
  setNavigator: (navigator: PageNavigator | null) => void;
  /**
   * The host rendered a different page (a new path — not a new query string,
   * so a filter never moves focus): focus its heading, announce its title.
   */
  pageChanged: () => void;
}

declare global {
  interface Window {
    budflowPageRuntime?: RuntimeApi;
    budflowActiveStore?: string | { id?: string } | null;
  }
}

const DISABLED = '[aria-disabled="true"]';
const DIALOG_TRIGGERS = '[data-modal-trigger],[data-drawer-trigger],[data-pb-site-modal]';

function readData(doc: Document): PageLiveData {
  const el = doc.getElementById('pb-live');
  if (!el?.textContent) return {};
  try {
    return JSON.parse(el.textContent) as PageLiveData;
  } catch {
    return {};
  }
}

function cookie(doc: Document, name: string): string | null {
  const hit = doc.cookie.split('; ').find((c) => c.startsWith(name + '='));
  if (!hit) return null;
  try {
    return decodeURIComponent(hit.slice(name.length + 1));
  } catch {
    return hit.slice(name.length + 1);
  }
}

/**
 * The visitor's store: what the host says, else the `selectedStore` cookie or
 * local storage (both storefronts and Budflow use that name), else the only
 * store a one-store site has. A value that names no store on the site counts
 * as none.
 */
export function activeStoreFrom(win: Window, data: PageLiveData): ActiveStore | null {
  const stores = data.stores ?? [];
  if (stores.length === 0) return null;
  const declared = win.budflowActiveStore;
  let id: string | null = typeof declared === 'string' ? declared : declared?.id ?? null;
  if (!id) id = cookie(win.document, 'selectedStore');
  if (!id) {
    try {
      id = win.localStorage.getItem('selectedStore');
    } catch {
      id = null;
    }
  }
  const found = id ? stores.find((s) => s.id === id) : undefined;
  if (found) return found;
  return stores.length === 1 ? stores[0] : null;
}

/** Apply one element's live rules. Off states are removed, never set "false". */
export function applyLiveStates(el: Element, win: Window, data: PageLiveData, now: number): void {
  let rules;
  try {
    rules = readStateWhen(JSON.parse(el.getAttribute('data-pb-when') || '[]'));
  } catch {
    return;
  }
  const ctx = { now, store: activeStoreFrom(win, data), timezone: data.timezone };
  const on = new Set(rules.filter((r) => evaluateStateWhen(r, ctx)).map((r) => r.state));
  const governed = new Set(rules.map((r) => r.state));
  if (governed.has('disabled')) {
    // The reason is read out only while disabled (data-pb-reason → its id).
    const reason = el.getAttribute('data-pb-reason');
    if (on.has('disabled')) {
      el.setAttribute('aria-disabled', 'true');
      if (reason) el.setAttribute('aria-describedby', reason);
    } else {
      el.removeAttribute('aria-disabled');
      if (reason) el.removeAttribute('aria-describedby');
    }
  }
  // A scheduled element (SCHEDULING_PLAN.md §2.3): shown while its `visible`
  // rule holds, hidden otherwise — both attributes, since the stylesheet
  // backs `data-pb-hidden` and the browser backs `hidden`.
  if (governed.has('visible')) {
    if (on.has('visible')) {
      el.removeAttribute('hidden');
      el.removeAttribute('data-pb-hidden');
    } else {
      el.setAttribute('hidden', '');
      el.setAttribute('data-pb-hidden', '');
    }
  }
  const current = (el.getAttribute('data-pb-state') || '').split(/\s+/).filter(Boolean);
  // Keep states this element's live rules don't govern (decided elsewhere).
  const next = current.filter((s) => !governed.has(s));
  for (const s of on) if (s !== 'disabled' && s !== 'visible' && !next.includes(s)) next.push(s);
  if (next.length) el.setAttribute('data-pb-state', next.join(' '));
  else el.removeAttribute('data-pb-state');
}

/** `aria-current` for every builder link, on a plain-HTML site. */
export function applyCurrent(doc: Document, data: PageLiveData, path: string): void {
  const redirects = data.redirects ? normaliseRedirects(data.redirects) : undefined;
  doc.querySelectorAll('[data-pb-current]').forEach((el) => {
    const href = el.getAttribute('href');
    const exactOnly = el.getAttribute('data-pb-current') === 'exact';
    const match = matchPath(href, path, { exactOnly, siteHosts: data.siteHosts, redirects });
    if (match) el.setAttribute('aria-current', match);
    else el.removeAttribute('aria-current');
  });
}

/**
 * The in-app path a click on `a` should navigate to, or null when the browser
 * must handle it: another tab, a download, a file, another site, a place on
 * this page, a modified or non-primary click, or a click already claimed
 * (an action, a disabled control). Only builder page links qualify.
 */
export function clientNavigationTarget(e: MouseEvent, win: Window): string | null {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
  const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
  if (!a || !a.hasAttribute('data-pb-current')) return null;
  // A placeholder (`#`) or this very address: nothing to navigate to.
  if ((a.getAttribute('href') || '').startsWith('#')) return null;
  if (a.pathname + a.search === win.location.pathname + win.location.search && !a.hash) return null;
  if (a.closest(DISABLED)) return null;
  if ((a.target && a.target !== '_self') || a.hasAttribute('download') || /\bexternal\b/.test(a.rel)) return null;
  if (a.origin !== win.location.origin) return null;
  if (/\.[a-z0-9]{2,5}$/i.test(a.pathname)) return null;
  if (a.hash && a.pathname === win.location.pathname && a.search === win.location.search) return null;
  return a.pathname + a.search + a.hash;
}

/** Focus the new page's heading (else <main>) and announce its title. */
export function announcePage(doc: Document): void {
  const target = (doc.querySelector('main h1') || doc.querySelector('h1') || doc.querySelector('main')) as HTMLElement | null;
  if (target) {
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }
  let region = doc.getElementById('pb-route-announcer');
  if (!region) {
    region = doc.createElement('div');
    region.id = 'pb-route-announcer';
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    region.setAttribute('style', 'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0');
    doc.body.appendChild(region);
  }
  // Cleared first, so the same title twice is still announced.
  region.textContent = '';
  const title = doc.title || target?.textContent?.trim() || '';
  region.textContent = title;
}

/**
 * Run `fn` once, after the next paint — or after 50 ms when there is no paint
 * coming (a background tab never fires requestAnimationFrame, and a visitor who
 * navigates then switches tab must not get the focus move and the announcement
 * only when they come back).
 */
export function afterPaint(win: Window, fn: () => void): void {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    fn();
  };
  win.requestAnimationFrame(run);
  win.setTimeout(run, 50);
}

type Playable = HTMLElement & {
  __pbPlaying?: boolean;
  __pbKept?: boolean;
  __pbBound?: boolean;
  __pbCount?: CountParts | null;
  __pbCountRaf?: number;
};

/** A CSS animation running on exactly this element (not a child's, not a transition). */
function animationsOn(target: Element): Animation[] {
  if (typeof (target as HTMLElement).getAnimations !== 'function') return [];
  return (target as HTMLElement).getAnimations().filter((a) => {
    if (!('animationName' in a)) return false; // a CSS transition (a hover look) is not ours
    const timing = a.effect?.getTiming?.();
    return !timing || timing.iterations !== Infinity;
  });
}

/** The element's own entrance — never its children's entrances or its hover transitions. */
function ownAnimations(el: Element): Animation[] {
  return animationsOn(el);
}

/** A letters reveal's per-character animations. */
function charAnimations(el: Element): Animation[] {
  return Array.from(el.querySelectorAll('.pb-char')).flatMap(animationsOn);
}

function tweenCount(el: Playable, win: Window, toValue: number): void {
  const parts = el.__pbCount;
  if (!parts) return;
  if (el.__pbCountRaf) win.cancelAnimationFrame(el.__pbCountRaf);
  const text = el.textContent ?? '';
  const m = /-?[\d.]+/.exec(text.slice(parts.prefix.length));
  const start = m ? Number(m[0]) : parts.from;
  const dur = Number(el.getAttribute('data-pb-count-dur')) || 1400;
  const delay = el.__pbPlaying ? Number(el.getAttribute('data-pb-count-delay')) || 0 : 0;
  const began = Date.now() + delay;
  const step = () => {
    const p = Math.min(1, Math.max(0, (Date.now() - began) / dur));
    const eased = 1 - Math.pow(1 - p, 3);
    el.textContent = formatCount(start + (toValue - start) * eased, parts);
    if (p < 1) el.__pbCountRaf = win.requestAnimationFrame(step);
  };
  el.__pbCountRaf = win.requestAnimationFrame(step);
}

/**
 * Nothing will ever play it (a pointer that can't hover, a source not on the
 * page): show it at rest — its entrance finished, its letters shown, its
 * number left as written — rather than held invisible at its start.
 */
export function showAtRest(el: Playable): void {
  el.__pbKept = true;
  for (const a of ownAnimations(el)) a.finish();
  if (el.classList.contains('pb-letters')) el.classList.add('pb-in-view', 'pb-anim-done');
}

/**
 * Show a count's starting number. Separate from binding so a React host can
 * do it after hydration (a rewrite before it would make the server text and
 * the hydrated text disagree).
 */
export function primeCount(el: Playable): void {
  if (el.__pbCount !== undefined || el.__pbKept || !el.__pbBound || !el.hasAttribute('data-pb-count-to')) return;
  el.__pbCount = countParts(el);
  if (el.__pbCount && !el.__pbPlaying) el.textContent = formatCount(el.__pbCount.from, el.__pbCount);
}

/**
 * Play `el` forward (on) or end it (off): played back from wherever it is
 * (`yes`), straight back to the start (`no`), or kept (`stay`, once).
 */
export function setPlaying(el: Playable, on: boolean, win: Window): void {
  // "Stay": it played once and keeps its end look — nothing more to do.
  if (el.__pbKept) return;
  if (!!el.__pbPlaying === on) return;
  const ending = el.getAttribute('data-pb-play-reverse');
  if (!on && ending === 'stay') {
    el.__pbKept = true;
    return;
  }
  el.__pbPlaying = on;
  if (on) primeCount(el);
  const reverse = ending === 'yes';
  const letters = el.classList.contains('pb-letters');

  if (on) {
    for (const a of ownAnimations(el)) {
      a.playbackRate = 1;
      a.play();
    }
    if (letters) {
      el.classList.remove('pb-anim-done');
      if (el.classList.contains('pb-in-view')) {
        // Still playing back: turn the characters round where they are.
        for (const a of charAnimations(el)) {
          a.playbackRate = 1;
          a.play();
        }
      } else el.classList.add('pb-in-view');
    }
  } else if (reverse) {
    for (const a of ownAnimations(el)) {
      a.playbackRate = -1;
      a.play();
    }
    if (letters) {
      const chars = charAnimations(el);
      for (const a of chars) {
        a.playbackRate = -1;
        a.play();
      }
      // Hide the letters only once they have played back (unless it played
      // forward again meanwhile); with nothing playing, at once.
      if (chars.length === 0) el.classList.remove('pb-in-view');
      else {
        Promise.all(chars.map((a) => a.finished))
          .then(() => { if (!el.__pbPlaying) el.classList.remove('pb-in-view'); })
          .catch(() => undefined);
      }
    }
  } else {
    for (const a of ownAnimations(el)) {
      a.pause();
      a.currentTime = 0;
    }
    if (letters) el.classList.remove('pb-in-view', 'pb-anim-done');
  }

  if (el.__pbCount) {
    if (on) tweenCount(el, win, el.__pbCount.to);
    else if (reverse) tweenCount(el, win, el.__pbCount.from);
    else {
      if (el.__pbCountRaf) win.cancelAnimationFrame(el.__pbCountRaf);
      el.textContent = formatCount(el.__pbCount.from, el.__pbCount);
    }
  }
}

/** Wire one element to its event on its source. */
export function bindPlay(el: Playable, win: Window): void {
  const source = playSource(el);
  const event = el.getAttribute('data-pb-play-on') ?? '';
  const canHover = !win.matchMedia || win.matchMedia('(hover: hover)').matches;
  if (!source || (event === 'hover' && !canHover)) {
    showAtRest(el);
    return;
  }
  el.__pbBound = true;
  const set = (on: boolean) => setPlaying(el, on, win);
  const disabled = () => source.getAttribute('aria-disabled') === 'true';
  if (event === 'hover') {
    source.addEventListener('pointerenter', () => { if (!disabled()) set(true); });
    source.addEventListener('pointerleave', () => set(false));
  } else if (event === 'pressed') {
    source.addEventListener('pointerdown', () => { if (!disabled()) set(true); });
    for (const end of ['pointerup', 'pointercancel', 'pointerleave']) source.addEventListener(end, () => set(false));
    source.addEventListener('keydown', (e) => {
      const k = (e as KeyboardEvent).key;
      if ((k === 'Enter' || k === ' ') && !disabled()) set(true);
    });
    source.addEventListener('keyup', () => set(false));
  } else if (event === 'focus') {
    source.addEventListener('focusin', (e) => {
      const t = e.target as Element;
      if (t.matches?.(':focus-visible')) set(true);
    });
    source.addEventListener('focusout', (e) => {
      const next = (e as FocusEvent).relatedTarget as Node | null;
      if (!next || !source.contains(next)) set(false);
    });
  } else {
    const check = () => set(stateIsOn(event, source));
    const Observer = (win as Window & typeof globalThis).MutationObserver;
    if (typeof Observer === 'function') {
      new Observer(check).observe(source, {
        attributes: true,
        attributeFilter: ['aria-current', 'aria-pressed', 'aria-expanded', 'aria-disabled', 'data-pb-state'],
      });
    }
    check();
  }
}

function isActivationKey(e: KeyboardEvent): boolean {
  return e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar';
}

export function installPageRuntime(win: Window): RuntimeApi {
  if (win.budflowPageRuntime) return win.budflowPageRuntime;
  const doc = win.document;
  let data = readData(doc);

  // 1. Disabled blocks activation, before any other listener (window, capture).
  const block = (e: Event) => {
    const target = e.target as Element | null;
    const disabled = target?.closest?.(DISABLED);
    if (!disabled) return;
    if (e.type === 'keydown' && !isActivationKey(e as KeyboardEvent)) return;
    // A link's own Enter, a button's Space and Enter, and every click.
    e.preventDefault();
    e.stopImmediatePropagation();
  };
  win.addEventListener('click', block, true);
  win.addEventListener('keydown', block, true);
  // Space activates a <button> on keyup in some browsers.
  win.addEventListener('keyup', (e) => {
    if ((e as KeyboardEvent).key === ' ' && (e.target as Element | null)?.closest?.(DISABLED)) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, true);

  // 2. Live states.
  const refresh = () => {
    const now = Date.now();
    doc.querySelectorAll('[data-pb-when]').forEach((el) => applyLiveStates(el, win, data, now));
  };
  let pending = false;
  const refreshSoon = () => {
    if (pending) return;
    pending = true;
    win.setTimeout(() => {
      pending = false;
      refresh();
    }, 0);
  };
  const tick = () => {
    refresh();
    // Re-check on the minute, so "opens at 9am" flips at 9:00, not 9:00:59.
    win.setTimeout(tick, 60_000 - (Date.now() % 60_000) + 50);
  };
  for (const name of ['store:change', 'storechange']) win.addEventListener(name, refreshSoon);
  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'visible') refreshSoon();
  });
  // New content (a React host swapping pages, a list loading more) carries
  // its own rules; check it as it arrives.
  const Observer = (win as Window & typeof globalThis).MutationObserver;
  if (typeof Observer === 'function') {
    new Observer((records: MutationRecord[]) => {
      for (const r of records) {
        for (const n of Array.from(r.addedNodes) as Node[]) {
          if (n.nodeType === 1 && ((n as Element).matches('[data-pb-when]') || (n as Element).querySelector('[data-pb-when]'))) {
            refreshSoon();
            return;
          }
        }
      }
    }).observe(doc.documentElement, { childList: true, subtree: true });
  }

  // 3. A popup or drawer trigger is expanded while its dialog is open.
  const opened = new Map<HTMLDialogElement, Element>();
  // Capture phase, so the snapshot of open dialogs is taken before the site's
  // own handler opens one (a disabled trigger was already stopped above).
  win.addEventListener('click', (e) => {
    const trigger = (e.target as Element | null)?.closest?.(DIALOG_TRIGGERS);
    if (!trigger || trigger.closest(DISABLED)) return;
    const before = new Set(Array.from(doc.querySelectorAll('dialog[open]')));
    win.setTimeout(() => {
      doc.querySelectorAll('dialog[open]').forEach((d) => {
        if (before.has(d)) return;
        opened.set(d as HTMLDialogElement, trigger);
        trigger.setAttribute('aria-expanded', 'true');
        // Also watch `open` itself: the `close` event is held back in a
        // background tab, and a dialog can be closed by removing the
        // attribute, which fires no event at all.
        if (typeof Observer === 'function') {
          const watch = new Observer(() => {
            if ((d as HTMLDialogElement).open) return;
            watch.disconnect();
            if (opened.get(d as HTMLDialogElement) !== trigger) return;
            opened.delete(d as HTMLDialogElement);
            trigger.setAttribute('aria-expanded', 'false');
          });
          watch.observe(d, { attributes: true, attributeFilter: ['open'] });
        }
      });
    }, 0);
  }, true);
  // `close` doesn't bubble; a capture listener on the document still hears it.
  doc.addEventListener('close', (e) => {
    const trigger = opened.get(e.target as HTMLDialogElement);
    if (!trigger) return;
    opened.delete(e.target as HTMLDialogElement);
    trigger.setAttribute('aria-expanded', 'false');
  }, true);

  // 4. Plain-HTML sites: current page after history navigation, and prefetch.
  const html = () => data.host === 'html';
  const syncCurrent = () => {
    if (html()) applyCurrent(doc, data, win.location.pathname);
  };
  for (const method of ['pushState', 'replaceState'] as const) {
    const original = win.history[method].bind(win.history);
    win.history[method] = ((...args: Parameters<History['pushState']>) => {
      const result = original(...args);
      syncCurrent();
      return result;
    }) as History['pushState'];
  }
  win.addEventListener('popstate', syncCurrent);
  // 5. Client navigation, when a host has registered its router.
  let navigator: PageNavigator | null = null;
  doc.addEventListener('click', (e) => {
    if (!navigator) return;
    const target = clientNavigationTarget(e as MouseEvent, win);
    if (!target) return;
    e.preventDefault();
    navigator.navigate(target);
  });
  const prefetchFor = (e: Event) => {
    if (!navigator?.prefetch) return;
    const a = (e.target as Element | null)?.closest?.('a[href][data-pb-current]') as HTMLAnchorElement | null;
    if (!a || a.origin !== win.location.origin || a.pathname === win.location.pathname) return;
    navigator.prefetch(a.pathname + a.search);
  };
  doc.addEventListener('pointerover', prefetchFor, { passive: true });
  doc.addEventListener('focusin', prefetchFor);

  const prefetched = new Set<string>();
  doc.addEventListener('pointerover', (e) => {
    if (!html()) return;
    const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!a || a.target === '_blank' || a.hasAttribute('download') || a.closest(DISABLED)) return;
    if (a.origin !== win.location.origin || a.pathname === win.location.pathname) return;
    const url = a.pathname + a.search;
    if (prefetched.has(url)) return;
    prefetched.add(url);
    const link = doc.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    doc.head.appendChild(link);
  }, { passive: true });

  // 6. Play triggers.
  const motionOk = !win.matchMedia || !win.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (motionOk) doc.documentElement.classList.add('pb-play-ready');
  const bound = new WeakSet<Element>();
  const primeCounts = () => doc.querySelectorAll('[data-pb-play-on][data-pb-count-to]').forEach((el) => primeCount(el as Playable));
  const bindPlays = (prime: boolean) => {
    if (!motionOk) return;
    doc.querySelectorAll('[data-pb-play-on]').forEach((el) => {
      if (bound.has(el)) return;
      bound.add(el);
      bindPlay(el as HTMLElement, win);
    });
    if (prime) primeCounts();
  };
  // A React host's markup is hydrated after this script runs: rewriting a
  // count's text now would make React's server and client text disagree. It
  // primes from refresh() / setData(), which it calls after hydration.
  bindPlays(data.host !== 'react');
  if (typeof Observer === 'function') {
    new Observer((records: MutationRecord[]) => {
      for (const r of records) {
        for (const n of Array.from(r.addedNodes) as Node[]) {
          if (n.nodeType === 1 && ((n as Element).matches('[data-pb-play-on]') || (n as Element).querySelector('[data-pb-play-on]'))) {
            // Content added after load is the client's own: no hydration to upset.
            bindPlays(true);
            return;
          }
        }
      }
    }).observe(doc.documentElement, { childList: true, subtree: true });
  }

  // 7. Loops (loop-effect.ts) pause off screen: the page is marked
  // `.pb-loop-ready` and each `[data-pb-loop="pause"]` element carries
  // `data-pb-inview` while it is on screen. Without IntersectionObserver the
  // page is never marked, so loops simply play.
  const IO = (win as Window & typeof globalThis).IntersectionObserver;
  const watchedLoops = new WeakSet<Element>();
  const loopIo = typeof IO === 'function'
    ? new IO((entries) => {
      for (const en of entries) {
        if (en.isIntersecting) en.target.setAttribute('data-pb-inview', '');
        else en.target.removeAttribute('data-pb-inview');
      }
    })
    : null;
  const watchLoops = () => {
    if (!loopIo) return;
    doc.querySelectorAll('[data-pb-loop="pause"]').forEach((el) => {
      if (watchedLoops.has(el)) return;
      watchedLoops.add(el);
      loopIo.observe(el);
    });
  };
  if (loopIo) {
    doc.documentElement.classList.add('pb-loop-ready');
    watchLoops();
    if (typeof Observer === 'function') {
      // Rescan only when a loop arrives, as the play triggers do; a loop that
      // leaves the page stops being watched (client navigation swaps pages).
      const LOOP = '[data-pb-loop="pause"]';
      const loopsIn = (n: Node): Element[] =>
        n.nodeType === 1 ? [...((n as Element).matches(LOOP) ? [n as Element] : []), ...Array.from((n as Element).querySelectorAll(LOOP))] : [];
      new Observer((records: MutationRecord[]) => {
        let added = false;
        for (const r of records) {
          for (const n of Array.from(r.removedNodes) as Node[]) {
            for (const el of loopsIn(n)) {
              if (el.isConnected) continue;
              loopIo.unobserve(el);
              watchedLoops.delete(el);
            }
          }
          if (!added) added = (Array.from(r.addedNodes) as Node[]).some((n) => loopsIn(n).length > 0);
        }
        if (added) watchLoops();
      }).observe(doc.documentElement, { childList: true, subtree: true });
    }
  }

  // 8. Slide shows (style-system/slides-effect.ts, slides.ts): each
  // `[data-pb-slides]` box is bound once. It adds to the DOM (the wrap's copy
  // of the first slide, the dots), so a React host waits for refresh() after
  // hydration, as the count-ups do; a plain page binds now.
  const bindShows = () => bindSlideShows(doc, win);
  if (data.host !== 'react') bindShows();
  if (typeof Observer === 'function') {
    new Observer((records: MutationRecord[]) => {
      for (const r of records) {
        for (const n of Array.from(r.addedNodes) as Node[]) {
          if (n.nodeType === 1 && ((n as Element).matches('[data-pb-slides]') || (n as Element).querySelector('[data-pb-slides]'))) {
            bindShows();
            return;
          }
        }
      }
    }).observe(doc.documentElement, { childList: true, subtree: true });
  }

  // 9. Cycle words: each `[data-pb-cycle]` text is bound once. It rewrites
  // the element's text, so a React host waits for refresh() after hydration,
  // as the count-ups and slide shows do; a plain page binds now.
  const bindCycles = () => bindCycleWords(doc, win);
  if (data.host !== 'react') bindCycles();
  if (typeof Observer === 'function') {
    new Observer((records: MutationRecord[]) => {
      for (const r of records) {
        for (const n of Array.from(r.addedNodes) as Node[]) {
          if (n.nodeType === 1 && ((n as Element).matches(CYCLE_SELECTOR) || (n as Element).querySelector(CYCLE_SELECTOR))) {
            bindCycles();
            return;
          }
        }
      }
    }).observe(doc.documentElement, { childList: true, subtree: true });
  }

  const api: RuntimeApi = {
    refresh: () => {
      refresh();
      bindPlays(true);
      watchLoops();
      bindShows();
      bindCycles();
    },
    setData: (next) => {
      data = next;
      refresh();
      syncCurrent();
      bindPlays(true);
      bindShows();
      bindCycles();
    },
    setNavigator: (next) => {
      navigator = next;
    },
    pageChanged: () => {
      // After the host's paint, so the new heading and title are in place.
      afterPaint(win, () => announcePage(doc));
    },
  };
  win.budflowPageRuntime = api;
  tick();
  syncCurrent();
  return api;
}
